/**
 * Sign-up conversion tracking.
 *
 * Fires exactly one `sign_up` event per account, at the moment the account
 * first has a real session — so it covers email/password AND Google sign-ups,
 * and it is only counted once the account genuinely exists (not when the form
 * is submitted). The event lands in GA4 (via gtag) and on the GTM dataLayer,
 * where it can be marked as a key event / conversion.
 */

import type { User } from "@supabase/supabase-js";
import { trackEvent } from "@/lib/analytics";
import { getAdReferral, logAdSignupConversion } from "@/lib/adTracking";
import { getUtm } from "@/lib/utmAttribution";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "statlign_signup_tracked_v1";
/** An account counted as "just created" within this window of its creation. */
const NEW_ACCOUNT_WINDOW_MS = 30 * 60 * 1000;

const trackedThisSession = new Set<string>();

function alreadyTracked(userId: string): boolean {
  if (trackedThisSession.has(userId)) return true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    return Array.isArray(ids) && ids.includes(userId);
  } catch {
    return false;
  }
}

function remember(userId: string) {
  trackedThisSession.add(userId);
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const ids = raw ? (JSON.parse(raw) as string[]) : [];
    const next = Array.isArray(ids) ? ids.concat(userId).slice(-50) : [userId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — session guard still prevents duplicates */
  }
}

/**
 * Record a completed sign-up. Safe to call on every auth event: it no-ops for
 * returning users and for accounts already counted.
 */
export function trackSignUpCompletion(user: User | null | undefined) {
  if (!user || typeof window === "undefined") return;
  const createdAt = user.created_at ? Date.parse(user.created_at) : NaN;
  if (!Number.isFinite(createdAt)) return;
  if (Date.now() - createdAt > NEW_ACCOUNT_WINDOW_MS) return; // returning user
  if (alreadyTracked(user.id)) return;
  remember(user.id);

  const method =
    (user.app_metadata?.provider as string | undefined) ??
    (user.identities?.[0]?.provider as string | undefined) ??
    "email";

  // Ad-to-signup attribution: if this visitor clicked an ad within the
  // attribution window, the conversion carries the network/placement that
  // brought them in, so ad spend can be judged on real accounts created.
  const referral = getAdReferral();
  const utm = getUtm();

  trackEvent("sign_up", {
    method,
    account_age_seconds: Math.round((Date.now() - createdAt) / 1000),
    ...(utm ? { utm_source: utm.utm_source, utm_medium: utm.utm_medium, utm_campaign: utm.utm_campaign, utm_content: utm.utm_content } : {}),
    ...(referral
      ? {
          ad_network: referral.network,
          ad_unit: referral.unit,
          ad_placement: referral.placement,
          ad_click_page: referral.path,
          ad_to_signup_seconds: Math.round((Date.now() - referral.at) / 1000),
        }
      : {}),
  });

  // Persist the sign-up source so Admin > Sign-up sources can tie each
  // account to the ad that brought it (first write wins; duplicates ignored).
  void supabase
    .from("signup_attribution")
    .insert({
      user_id: user.id,
      method,
      utm_source: utm?.utm_source ?? null,
      utm_medium: utm?.utm_medium ?? null,
      utm_campaign: utm?.utm_campaign ?? null,
      utm_content: utm?.utm_content ?? null,
      utm_term: utm?.utm_term ?? null,
    })
    .then(() => undefined, () => undefined);

  if (referral) {
    trackEvent("ad_signup_conversion", {
      ad_network: referral.network,
      ad_unit: referral.unit,
      ad_placement: referral.placement,
      ad_click_page: referral.path,
      method,
    });
    logAdSignupConversion({
      ad_network: referral.network,
      ad_unit: referral.unit,
      ad_placement: referral.placement,
      page_path: referral.path,
    });
  }
}
